/**
 * The header every screen carries, from `faithfully-kept-business/design/home/NoMembership.dc.html`:
 * a white bar on a hairline, the mark beside the name, and a 56px navy Call button, so the phone is
 * one tap away on every screen whatever else has failed. The bar runs the whole width, and its row
 * is the page's one column (`column.ts`), so at a desk the name and Call line up with the page.
 *
 * ON EVERY SIGNED-IN PLACE (`signedIn`, which the shell gives; slice A1, from the Settings canvas's
 * Settings-Main artboard in today's look):
 *
 * - THE MARK AND THE NAME ARE A LINK TO HOME, named "Faithfully Kept, Home", so the name she reads
 *   is in the name she hears.
 * - ACCOUNT, left of Call: an outlined button 56 by 56, a person over the word, the brand's gold
 *   border on white, leading to the page about her (`screens/Account.tsx`). On Account and its two
 *   pages it is drawn as the page she is on, a navy border on the pale navy, with
 *   `aria-current="page"`, since no tab of the nav is. Account is a page reached from here, as an
 *   invoice's is, and never a fifth place: the brief draws four.
 * - At 640px and wider, a desk, Account draws its person beside the word, and Call says the
 *   number, "Call (727) 337-7775", so it can be read where a phone link opens nothing; its name
 *   says the number at every width. Under 380px
 *   the name and the word step down a size; under 340px Account draws its person alone, and is
 *   still named "Account".
 *
 * The holding screen, drawn while nothing of the household has come down, before the grant
 * included, is a signed-in place with a home linked, and draws this header too (round five, slice
 * A4), so Account is on every signed-in screen that has one: Account answers there with the address
 * she signed in with and where she is signed in. The door, sign-in and the page that says to call
 * draw the header as it was: the name is no link and there is no Account, since nobody is signed
 * in at the first two, and at the third no home is linked, which Account would answer with that
 * same page; each has its own Sign out.
 *
 * It is drawn one layer above what covers the screen below it (`COVER_LAYER` in `Shell.tsx`), the
 * panel while she is taken to Stripe's page, so Call stays undimmed and live there; the name and
 * Account, each marked `data-coverable`, are made inert with the page while it is there, so Call is
 * still the one thing to press. A sheet, a layer higher, still dims the whole header.
 */
import { Link, useLocation } from 'react-router-dom';
import { PHONE_HREF, PHONE_SHOWN } from '../contact.ts';
import { ACCOUNT_PATH, HOME_PATH, onAccount } from '../paths.ts';
import { ACCOUNT } from '../words.ts';
import { BrandMark } from './BrandMark.tsx';
import { COLUMN } from './column.ts';

/** The name beside the mark: the lead size, a step down on the narrowest phones. */
const NAME =
  'truncate font-serif text-lead font-semibold text-text-brand max-[380px]:text-[1.125rem]';

/** Account's button: 56 square on a phone, its person over its word; wider at a desk. */
const ACCOUNT_BUTTON =
  'inline-flex size-14 shrink-0 flex-col items-center justify-center gap-px rounded-control ' +
  'border-2 text-[0.8125rem] leading-4 font-semibold text-text-brand ' +
  'max-[380px]:text-[0.75rem] sm:w-auto sm:flex-row sm:gap-1.5 sm:px-3 sm:text-small';

/** Account where it is not the page she is on: the brand's gold border on white. */
const ELSEWHERE = 'border-border-brand bg-surface-raised hover:bg-surface-accent-soft';

/** Account on its own pages: a navy border on the pale navy. */
const HERE = 'border-text-body bg-surface-brand-soft';

/** The person on Account's button, hidden from a screen reader, which reads the word. */
function Person() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.75 20c.9-3.75 3.75-5.75 7.25-5.75s6.35 2 7.25 5.75" />
    </svg>
  );
}

export function Header({ signedIn = false }: { readonly signedIn?: boolean }) {
  return (
    <header className="relative z-16 border-b border-border-subtle bg-surface-raised">
      <div className={`${COLUMN} flex items-center justify-between gap-2 px-5 py-2`}>
        {signedIn ? (
          <Link
            to={HOME_PATH}
            aria-label={ACCOUNT.brandHome}
            data-coverable
            className="flex min-h-tap min-w-0 items-center gap-2"
          >
            <BrandMark className="shrink-0 text-text-brand" />
            <span className={NAME}>Faithfully Kept</span>
          </Link>
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <BrandMark className="shrink-0 text-text-brand" />
            <span className={NAME}>Faithfully Kept</span>
          </div>
        )}
        <div className="flex shrink-0 items-center gap-1.5">
          {signedIn ? <AccountButton /> : null}
          <a
            href={PHONE_HREF}
            aria-label={`Call ${PHONE_SHOWN}`}
            className="inline-flex min-h-tap-call items-center justify-center rounded-control bg-surface-brand px-4 text-body font-semibold whitespace-nowrap text-text-on-brand hover:bg-surface-brand-strong"
          >
            Call
            {signedIn ? <span className="hidden sm:inline">&nbsp;{PHONE_SHOWN}</span> : null}
          </a>
        </div>
      </div>
    </header>
  );
}

/** Account, left of Call, drawn as the page she is on across Account's pages. */
function AccountButton() {
  const here = onAccount(useLocation().pathname);
  return (
    <Link
      to={ACCOUNT_PATH}
      aria-current={here ? 'page' : undefined}
      data-coverable
      className={`${ACCOUNT_BUTTON} ${here ? HERE : ELSEWHERE}`}
    >
      <Person />
      <span className="max-[340px]:sr-only">{ACCOUNT.name}</span>
    </Link>
  );
}
